// In src/pages/SermonArchivesPage.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, orderBy } from 'firebase/firestore';

import { Container, Typography, CircularProgress, Box, Button, Card, CardContent, CardActions, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow'; // For playing sermon

import SermonModal from '../components/SermonModal';

function SermonArchivesPage() {
  const { userProfile } = useAuth();
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isSermonModalOpen, setIsSermonModalOpen] = useState(false);
  const [selectedSermon, setSelectedSermon] = useState(null); // For editing

  const isLeadership = userProfile?.role === 'Leadership' || userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';

  // Fetch sermons in real-time, ordered by date
  useEffect(() => {
    if (!userProfile?.churchId) {
      setLoading(false);
      return;
    }

    const sermonsColRef = collection(db, 'churches', userProfile.churchId, 'sermons');
    const q = query(sermonsColRef, orderBy('date', 'desc')); // Order by date descending

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setSermons(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching sermons: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userProfile]);

  const handleAddSermon = () => {
    setSelectedSermon(null);
    setIsSermonModalOpen(true);
  };

  const handleEditSermon = (sermon) => {
    setSelectedSermon(sermon);
    setIsSermonModalOpen(true);
  };

  const handleSaveSermon = async (sermonData) => {
    if (!userProfile?.churchId) return;
    const sermonsColRef = collection(db, 'churches', userProfile.churchId, 'sermons');
    
    // sermonData.date is a JS Date object from DatePicker
    const dataToSave = {
        ...sermonData,
        churchId: userProfile.churchId,
    };

    try {
      if (selectedSermon?.id) {
        const docRef = doc(sermonsColRef, selectedSermon.id);
        await updateDoc(docRef, dataToSave);
      } else {
        await addDoc(sermonsColRef, dataToSave);
      }
      setIsSermonModalOpen(false);
      setSelectedSermon(null);
    } catch (error) {
      console.error("Error saving sermon:", error);
      throw error;
    }
  };

  const handleDeleteSermon = async (sermonId) => {
    if (!userProfile?.churchId || !sermonId) return;
    if (window.confirm('Are you sure you want to delete this sermon entry?')) {
      try {
        await deleteDoc(doc(db, 'churches', userProfile.churchId, 'sermons', sermonId));
        alert('Sermon deleted.');
      } catch (error) {
        console.error("Error deleting sermon:", error);
        alert('Failed to delete sermon.');
      }
    }
  };

  if (loading) {
    return <CircularProgress />;
  }
  if (!userProfile?.churchId) {
    return <Container><Typography>Please ensure your user profile has a church assigned to view sermon archives.</Typography></Container>;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Sermon Archives</Typography>
        {isLeadership && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddSermon}>
            Add Sermon
          </Button>
        )}
      </Box>

      {sermons.length === 0 && (
        <Typography>No sermons available in the archive at this time.</Typography>
      )}

      <Grid container spacing={3}>
        {sermons.map((sermon) => (
          <Grid item key={sermon.id} xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" component="h2">{sermon.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Speaker: {sermon.speaker}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Date: {sermon.date?.toDate().toLocaleDateString()}
                </Typography>
                {sermon.description && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {sermon.description}
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                {sermon.videoLink && (
                  <Button
                    size="small"
                    startIcon={<PlayArrowIcon />}
                    href={sermon.videoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                  >
                    Watch Sermon
                  </Button>
                )}
                {isLeadership && (
                  <Box>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditSermon(sermon)}>
                      Edit
                    </Button>
                    <Button size="small" startIcon={<DeleteIcon />} color="error" onClick={() => handleDeleteSermon(sermon.id)}>
                      Delete
                    </Button>
                  </Box>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <SermonModal
        open={isSermonModalOpen}
        onClose={() => setIsSermonModalOpen(false)}
        onSave={handleSaveSermon}
        sermon={selectedSermon}
      />
    </Container>
  );
}

export default SermonArchivesPage;